/**
 * Wave 27.2 — Supplier 360 (Phase B).
 *
 * Single-page drilldown for any supplier: YTD spend, project list,
 * top expense categories, lead-time stat, recent receipts, and a
 * Hidro nudge when spend is high.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Truck, Loader2, DollarSign, Calendar, Layers,
  TrendingUp, Sparkles, ExternalLink, Inbox, Receipt as ReceiptIcon,
  Briefcase, Mail, Phone, Globe, Tag,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const fmtMoney = (n, ccy = "ZAR") =>
  n == null ? "—" : `${ccy} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Supplier360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const allowed = role === "owner" || role === "admin" || role === "manager";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    api.get(`/suppliers/${id}/360`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e?.response?.status === 404) {
          toast.error("Supplier not found.");
          navigate("/suppliers");
        } else {
          toast.error("Couldn't load supplier 360.");
        }
      })
      .finally(() => setLoading(false));
  }, [id, allowed, navigate]);

  if (!allowed) return (
    <div className="hv-card p-8 text-center text-[#a0aabf] max-w-md mx-auto">
      <Truck className="w-8 h-8 mx-auto mb-2 text-[#647087]" />
      <div>Supplier 360 is for admins and managers.</div>
    </div>
  );
  if (loading) return (
    <div className="hv-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1FB6C1]" /></div>
  );
  if (!data) return null;

  const sup = data.supplier;
  const ccy = sup.currency || "ZAR";
  return (
    <div className="space-y-4" data-testid="supplier-360-page">
      {/* Header */}
      <div className="flex items-start gap-2 flex-wrap">
        <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-xs shrink-0" data-testid="sup360-back">
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#A78BFA]" />
            <span className="text-[10px] font-mono text-[#647087] uppercase">Supplier 360°</span>
            {sup.status && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold bg-emerald-500/15 text-emerald-400">
                {sup.status}
              </span>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold truncate">{sup.name}</h1>
          {sup.trading_name && <div className="text-xs text-[#a0aabf]">{sup.trading_name}</div>}
          <div className="flex items-center gap-3 text-[11px] text-[#647087] mt-1 flex-wrap">
            {sup.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {sup.phone}</span>}
            {sup.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {sup.email}</span>}
            {sup.website && <a className="inline-flex items-center gap-1 hover:text-[#1FB6C1]" href={sup.website} target="_blank" rel="noreferrer"><Globe className="w-3 h-3" /> {sup.website}</a>}
          </div>
        </div>
        <Link to={`/suppliers`} className="hv-btn hv-btn-ghost text-xs">
          All suppliers
        </Link>
      </div>

      {/* Hidro nudge */}
      {data.hidro_nudge && (
        <div className="hv-card !p-3 border-[#1FB6C1]/30 bg-gradient-to-br from-[#1FB6C1]/10 to-transparent flex items-start gap-2" data-testid="sup360-nudge">
          <Sparkles className="w-4 h-4 text-[#1FB6C1] shrink-0 mt-0.5" />
          <div className="text-xs text-[#d7dde8]">{data.hidro_nudge}</div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="YTD Spend"      value={fmtMoney(data.ytd_spend, ccy)}      icon={DollarSign} color="#10B981" testId="sup360-ytd" />
        <StatCard label="Lifetime spend" value={fmtMoney(data.lifetime_spend, ccy)} icon={TrendingUp} color="#1FB6C1" testId="sup360-lifetime" />
        <StatCard label="Receipts"        value={data.receipt_count.toLocaleString()} icon={ReceiptIcon} color="#A78BFA" testId="sup360-count" />
        <StatCard label="Avg receipt"    value={fmtMoney(data.avg_receipt, ccy)}    icon={Layers} color="#F59E0B" testId="sup360-avg" />
        <StatCard label="Projects"       value={data.project_count.toLocaleString()} icon={Briefcase} color="#0EA5E9" testId="sup360-projects-count" />
        <StatCard
          label="Avg lead time"
          value={data.avg_lead_time_days != null ? `${data.avg_lead_time_days}d` : "—"}
          icon={Calendar} color="#22C55E" testId="sup360-lead"
        />
        <StatCard label="Stocked items"  value={data.inventory_items_count.toLocaleString()} icon={Tag} color="#F43F5E" testId="sup360-inv" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Projects */}
        <div className="lg:col-span-2 hv-card !p-3" data-testid="sup360-projects">
          <div className="font-display font-semibold text-sm text-[#a0aabf] mb-2 inline-flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5" /> Projects ({data.projects.length})
          </div>
          {data.projects.length === 0 ? (
            <div className="text-[11px] text-[#647087] italic">No project history.</div>
          ) : (
            <ul className="space-y-1">
              {data.projects.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded hover:bg-white/[0.03]">
                  <Link to={`/projects/${p.id}`} className="flex-1 min-w-0 inline-flex items-center gap-2 hover:text-[#1FB6C1]">
                    <span className="font-medium text-white truncate">{p.title || p.id.slice(0, 8)}</span>
                    {p.lead_time_days != null && (
                      <span className="text-[10px] text-[#647087]">· {p.lead_time_days}d lead</span>
                    )}
                  </Link>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-xs text-white">{fmtMoney(p.total, ccy)}</div>
                    <div className="text-[10px] text-[#647087]">{p.count} receipt{p.count === 1 ? "" : "s"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top categories */}
        <div className="hv-card !p-3" data-testid="sup360-categories">
          <div className="font-display font-semibold text-sm text-[#a0aabf] mb-2 inline-flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Top categories
          </div>
          {data.top_categories.length === 0 ? (
            <div className="text-[11px] text-[#647087] italic">No history.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.top_categories.map((c) => {
                const pct = data.lifetime_spend > 0 ? (c.total / data.lifetime_spend) * 100 : 0;
                return (
                  <li key={c.id} className="text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-white">{c.name}</span>
                      <span className="font-mono text-[10px] text-[#a0aabf]">{fmtMoney(c.total, ccy)}</span>
                    </div>
                    <div className="h-1 bg-white/[0.05] rounded overflow-hidden mt-0.5">
                      <div className="h-full bg-[#1FB6C1]" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Recent receipts */}
      <div className="hv-card !p-3" data-testid="sup360-receipts">
        <div className="font-display font-semibold text-sm text-[#a0aabf] mb-2 inline-flex items-center gap-1.5">
          <ReceiptIcon className="w-3.5 h-3.5" /> Recent receipts
        </div>
        {data.recent_receipts.length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">No receipts captured yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {data.recent_receipts.map((r) => (
              <li key={r.id} className="grid grid-cols-12 gap-2 text-xs py-1.5">
                <div className="col-span-3 font-mono text-[#647087]">{r.date || "—"}</div>
                <div className="col-span-3 text-white truncate">{r.doc_number || "—"}</div>
                <div className="col-span-2 text-[#a0aabf] truncate">{r.category_name || "—"}</div>
                <div className="col-span-2 text-[#647087] truncate">{r.captured_by_name || ""}</div>
                <div className="col-span-2 text-right font-mono text-white">{fmtMoney(r.amount, r.currency || ccy)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="hv-card !p-2.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-[#647087] inline-flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" style={{ color }} />} {label}
      </div>
      <div className="font-mono text-base font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
